import { Slot } from "@radix-ui/react-slot";
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
  type RefObject,
} from "react";
import { cn } from "@/lib/utils";

/**
 * Minimal shadcn-style dropdown menu.
 *
 * The upstream shadcn `DropdownMenu` wraps `@radix-ui/react-dropdown-menu`,
 * which is NOT a dependency of this project (adding it would drag in the
 * whole `@radix-ui/react-menu` + popper tree and touch the shared, locked
 * node_modules / package-lock that CI's `npm ci` verifies). This project
 * only needs a click-to-open list of navigation links, so we implement the
 * same component API (`DropdownMenu` / `DropdownMenuTrigger` /
 * `DropdownMenuContent` / `DropdownMenuItem`) with plain React plus the
 * already-present `@radix-ui/react-slot` for the `asChild` pattern.
 */

interface DropdownMenuContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  contentId: string;
  triggerRef: RefObject<HTMLButtonElement | null>;
}

const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(
  null,
);

function useDropdownMenu(): DropdownMenuContextValue {
  const ctx = useContext(DropdownMenuContext);
  if (ctx === null) {
    throw new Error(
      "DropdownMenu subcomponents must be rendered inside <DropdownMenu>",
    );
  }
  return ctx;
}

export function DropdownMenu({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const contentId = useId();
  return (
    <DropdownMenuContext.Provider
      value={{ open, setOpen, contentId, triggerRef }}
    >
      <div className="relative inline-block text-left">{children}</div>
    </DropdownMenuContext.Provider>
  );
}

type DropdownMenuTriggerProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  asChild?: boolean;
};

export function DropdownMenuTrigger({
  asChild = false,
  onClick,
  ...props
}: DropdownMenuTriggerProps) {
  const { open, setOpen, contentId, triggerRef } = useDropdownMenu();
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      ref={triggerRef}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={open ? contentId : undefined}
      onClick={(event) => {
        onClick?.(event);
        setOpen(!open);
      }}
      {...(asChild ? {} : { type: "button" as const })}
      {...props}
    />
  );
}

type DropdownMenuContentProps = React.HTMLAttributes<HTMLDivElement> & {
  /** Horizontal edge the panel aligns to, relative to the trigger. */
  align?: "start" | "end";
};

export function DropdownMenuContent({
  className,
  align = "end",
  children,
  ...props
}: DropdownMenuContentProps) {
  const { open, setOpen, contentId, triggerRef } = useDropdownMenu();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node | null;
      if (target === null) return;
      if (ref.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, setOpen, triggerRef]);

  if (!open) return null;
  return (
    <div
      ref={ref}
      id={contentId}
      role="menu"
      className={cn(
        "absolute z-50 mt-1 min-w-[16rem] rounded-md border bg-popover p-1 text-popover-foreground shadow-md",
        align === "end" ? "right-0" : "left-0",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

type DropdownMenuItemProps = React.HTMLAttributes<HTMLElement> & {
  asChild?: boolean;
  disabled?: boolean;
};

/**
 * A single menu row. When `disabled`, the row is dimmed and swallows
 * clicks; callers that pass an `<a>` via `asChild` should also drop the
 * `href` so the anchor is inert (an anchor without href is unfocusable).
 */
export function DropdownMenuItem({
  className,
  asChild = false,
  disabled = false,
  onClick,
  ...props
}: DropdownMenuItemProps) {
  const { setOpen } = useDropdownMenu();
  const Comp = asChild ? Slot : "div";
  return (
    <Comp
      role="menuitem"
      aria-disabled={disabled || undefined}
      data-disabled={disabled ? "" : undefined}
      className={cn(
        "flex w-full cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent",
        disabled && "pointer-events-none opacity-50",
        className,
      )}
      onClick={(event: React.MouseEvent<HTMLElement>) => {
        if (disabled) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
        setOpen(false);
      }}
      {...props}
    />
  );
}
